import React, { createContext, useContext, useEffect, useState } from "react"
import SessionContext from "components/Common/SessionContext"

const FirebaseContext = createContext(null)

const features = {
    auth: true,
    database: false,
    firestore: true,
    storage: false,
    messaging: false,
    functions: true,
}

function useFirebase(fn) {
    const firebase = useContext(FirebaseContext)
    useEffect(() => {
        if (!firebase) {
            return
        }
        return fn(firebase)
    }, [firebase])
}

const FirebaseProvider = ({ children }) => {
    const [firebase, setFirebase] = useState(null)
    const session = useContext(SessionContext)

    useEffect(() => {
        if (!firebase && typeof window !== "undefined") {
            const app = import("firebase/app")
            const auth = features.auth ? import("firebase/auth") : null
            const database = features.database ? import("firebase/database") : null
            const firestore = features.firestore ? import("firebase/firestore") : null
            const storage = features.storage ? import("firebase/storage") : null
            const messaging = features.messaging ? import("firebase/messaging") : null
            const functions = features.functions ? import("firebase/functions") : null

            //react calls hook first; firebase hasn't initialized at that point. but when it gets to regular functions, firebase has init.
            //You must use firebase.auth().onAuthStateChanged to get the uid bcs .auth.currentUser.uid will NOT be available on page load. It shows up a microsecond after (it's asynchronous).
            //To do what you want simply build out a promise function and call it within .onAuthStateChanged and .then do your other function. You custom promise might look like:

            Promise.all([app, auth, database, firestore, storage, messaging, functions]).then(values => {
                const firebaseInstance = values[0]

                //initialize is synchronous. but determining current user state is async.

                firebaseInstance.initializeApp({
                    apiKey: process.env.GATSBY_FIREBASE_API_KEY,
                    authDomain: process.env.GATSBY_FIREBASE_AUTH_DOMAIN,
                    databaseURL: process.env.GATSBY_FIREBASE_DATABASE_URL,
                    projectId: process.env.GATSBY_FIREBASE_PROJECT_ID,
                    storageBucket: process.env.GATSBY_FIREBASE_STORAGE_BUCKET,
                    messagingSenderId: process.env.GATSBY_FIREBASE_MESSAGING_SENDER_ID,
                    appId: process.env.GATSBY_FIREBASE_APP_ID,
                })

                if (process.env.GATSBY_CURRENT_ENV === "dev") {
                    firebaseInstance.functions().useFunctionsEmulator("http://localhost:5001")
                }

                setFirebase(firebaseInstance)
                new Promise(resolve => {
                    //unsubscribe() what is this for?
                    const unsubscribe = firebaseInstance.auth().onAuthStateChanged(function(user) {
                        unsubscribe()
                        if (user) {
                            session.setUser({
                                email: user.email,
                                uid: user.uid,
                            })
                            resolve(true)
                        } else {
                            session.setUser({
                                email: "ANONYMOUS",
                                uid: "anonymous",
                            })
                            resolve(false)
                        }
                    })
                })
            })
        }
    }, [])

    return <FirebaseContext.Provider value={firebase}>{children}</FirebaseContext.Provider>
}

export default FirebaseContext
export { FirebaseProvider, useFirebase }

//issue: promise only guarantees that what's inside it won't be executed until the params resolve.
//so for example, if we call a chained promise, the chained one will get called after firebase is init, but the rest of the code will go on.
/* think this is why:
- at first, components render without firebase - since promise isn't resolved yet
- usehook is called because component is mounted
- promise is reolved, and passes that info to children -> children re-render. at that point, they can see firebase. 

so we could: once firebase resolves, then get auth user. update that with state.

SETSTATE SEEMS TO BE ASYNC...

*/
